/**
 * 料理の説明カード（図鑑のタイルや NEXT をタップすると開く）。
 *
 * 遊びながら料理の名前と中身を覚えられるように。名前だけでは何の料理か分からない品が多い。
 * まだ出会っていない料理は「？」のまま、どうすれば分かるかだけを書く。
 */

import type { GameContext } from '../game/GameContext';
import { getRewardStatus } from '../game/config/rewardStatus';
import type { StageItemDefinition } from '../game/config/types';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class DishCard {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private resumeOnClose = false;

  /** 「食べに行く」を押したとき。 */
  onOpenReward?: (item: StageItemDefinition) => void;
  onClose?: (resumeOnClose: boolean) => void;

  constructor(private readonly context: GameContext) {
    this.root = el('div', 'modal dish-card');
    this.root.dataset.visible = 'false';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-labelledby', 'dish-card-title');
    const backdrop = el('div', 'modal__backdrop');
    backdrop.addEventListener('click', () => this.hide());
    const card = el('div', 'modal__card');
    const head = el('div', 'modal__head');
    head.append(el('span'));
    this.closeBtn = el('button', 'btn btn--icon');
    this.closeBtn.type = 'button';
    this.closeBtn.setAttribute('aria-label', '閉じる');
    this.closeBtn.append(el('span', undefined, '✕'));
    this.closeBtn.addEventListener('click', () => this.hide());
    head.append(this.closeBtn);
    this.body = el('div', 'modal__body dish-card__body');
    card.append(head, this.body);
    this.root.append(backdrop, card);
    document.body.append(this.root);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.hide();
    });
  }

  open(item: StageItemDefinition, resumeOnClose: boolean): void {
    this.resumeOnClose = resumeOnClose;
    const { storage, icons } = this.context;
    const revealed = storage.isRevealed(item.id);
    const nodes: HTMLElement[] = [];

    if (!revealed) {
      nodes.push(
        el('div', 'dish-card__mystery', '？'),
        el('p', 'dish-card__level', `Lv.${item.level}`),
        Object.assign(el('h2', 'dish-card__name', 'まだ出会っていない料理'), { id: 'dish-card-title' }),
        el(
          'p',
          'dish-card__desc',
          item.level <= 6
            ? 'NEXT に並ぶか、合体で作ると分かります。'
            : 'ひとつ下の料理どうしを合体させると現れます。',
        ),
      );
    } else {
      const img = el('img', 'dish-card__img');
      img.src = icons.iconUrl(item, 256);
      img.alt = item.name;
      img.width = 160;
      img.height = 160;
      nodes.push(
        img,
        el('p', 'dish-card__level', `Lv.${item.level}`),
        Object.assign(el('h2', 'dish-card__name', item.name), { id: 'dish-card-title' }),
      );
      if (item.description) nodes.push(el('p', 'dish-card__desc', item.description));
      // ひとこと説明だけでは短い品が多いので、「食べに行く」で出している見出しと豆知識も出す（2026-10-04）
      const more = [item.reward?.headline, item.reward?.tip].filter(
        (text): text is string => Boolean(text) && text !== item.description,
      );
      if (more.length > 0) nodes.push(el('p', 'dish-card__more', more.join('')));
      if (item.source) {
        const src = el('p', 'tips-source');
        const a = el('a');
        a.href = item.source;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        try {
          a.textContent = new URL(item.source).hostname.replace(/^www\./, '');
        } catch {
          a.textContent = item.source;
        }
        src.append('出典：', a);
        nodes.push(src);
      }
      const status = getRewardStatus(item, storage.seenItemIds, storage.totalScore);
      if (status.available && item.reward) {
        const go = el('button', 'btn btn--primary', '🍽 食べに行く');
        go.type = 'button';
        go.addEventListener('click', () => {
          this.root.dataset.visible = 'false';
          this.onOpenReward?.(item);
        });
        nodes.push(go);
      }
    }
    this.body.replaceChildren(...nodes);
    this.root.dataset.visible = 'true';
    this.closeBtn.focus();
  }

  hide(): void {
    if (!this.isOpen()) return;
    this.root.dataset.visible = 'false';
    this.onClose?.(this.resumeOnClose);
  }

  isOpen(): boolean {
    return this.root.dataset.visible === 'true';
  }
}
